import React, { useEffect, useRef } from "react";

const SITE_KEY = "0x4AAAAAACw5cIkrDxgNyfLG";
const SCRIPT_ID = "cf-turnstile-script";

type Props = {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export const TurnstileWidget: React.FC<Props> = ({
  onVerify,
  onExpire,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  // Store callbacks in refs to avoid re-renders
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useEffect(() => {
    mountedRef.current = true;

    const doRender = () => {
      if (!mountedRef.current || !containerRef.current || !window.turnstile) return;
      // Only render once
      if (widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => onVerifyRef.current(token),
        "expired-callback": () => onExpireRef.current?.(),
        "error-callback": () => onErrorRef.current?.(),
        theme: "auto",
      });
    };

    // If script already loaded
    if (window.turnstile) {
      doRender();
      return;
    }

    // Only add script once globally
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad";
      script.async = true;
      document.head.appendChild(script);
    }

    // Poll for turnstile to be ready (handles both fresh load and race conditions)
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        doRender();
      }
    }, 100);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, []); // Empty deps — render once, never re-run

  return <div ref={containerRef} className="flex justify-center" />;
};
